/**
 * Cache collection that stores cached items by key
 */
type CacheCollection<R> = Map<string | number, CacheItem<R>>;

/**
 * Global cache storage, keyed by collection name
 */
const cachedData = new Map<string, CacheCollection<object>>();

/**
 * Configuration parameters for cache wrapper
 * @template P - Type of parameters passed to the cached function
 */
export type CacheWrapperParams<P> = {
  /** Unique identifier for the cached item within the collection */
  itemKey: string | number;
  /** Unique identifier for the cache collection */
  collectionKey: string;
  /** Function to generate a hash from parameters for cache validation */
  hashGetter: (params: P) => string;
  /** Array of parameter keys that should always be passed through from params to cached result */
  passThrough?: ReadonlyArray<keyof P>;
};

/**
 * Cached item structure
 * @template T - Type of the cached data
 */
interface CacheItem<T> {
  /** Hash value used to validate cache freshness */
  hash: string;
  /** The cached data */
  data: T;
}

/**
 * Creates a caching wrapper around an async function
 * @template P - Type of parameters
 * @template R - Type of result (must be an object to support passThrough)
 * @param config - Cache configuration parameters
 * @param cb - The async function to cache
 * @returns A cached version of the function
 */
export const cacheWrapper =
  <P extends object, R extends object>(
    {
      collectionKey,
      itemKey,
      hashGetter,
      passThrough = [],
    }: CacheWrapperParams<P>,
    cb: (params: P) => Promise<R>,
  ) =>
  async (params: P): Promise<R> => {
    if (!cachedData.has(collectionKey)) {
      cachedData.set(collectionKey, new Map<string | number, CacheItem<R>>());
    }
    const cacheCollection = cachedData.get(collectionKey) as CacheCollection<R>;
    const hash = hashGetter(params);
    const cache = cacheCollection.get(itemKey) as CacheItem<R> | undefined;

    // Return cached data if hash matches
    if (cache?.hash === hash) {
      // Apply passThrough parameters to cached data
      const result = { ...cache.data };
      for (const key of passThrough) {
        if (key in params && key in result) {
          (result as Record<string, unknown>)[key as string] = params[key];
        }
      }
      return result;
    }

    // Compute and cache new result
    const result = await cb(params);
    cacheCollection.set(itemKey, {
      hash,
      data: result,
    });
    return result;
  };
